import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { EventInboxApiFactory } from '../api/eventInboxApi';
import type { EventInboxStats } from '../eventInboxTypes';

interface UseGetEventInboxStatsResult {
  data: EventInboxStats | null;
  loading: boolean;
  error: ApiError | Error | null;
  refetch: () => void;
}

/** Fetches the KPI counts from `GET /event-inbox/stats`. */
export function useGetEventInboxStats(): UseGetEventInboxStatsResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => EventInboxApiFactory(client), [client]);

  const [data, setData] = useState<EventInboxStats | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [revision, setRevision] = useState(0);

  const refetch = useCallback(() => setRevision(r => r + 1), []);

  useEffect(() => {
    const ctrl = new AbortController();
    setError(null);

    api
      .getEventsStats({ signal: ctrl.signal })
      .then(setData)
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => ctrl.abort();
  }, [api, revision]);

  return { data, loading, error, refetch };
}
